/**
 * Security Headers Provider Component
 * MOBILE SAFE: Provides context without initializing the hook (hook causes mobile crashes)
 * Security headers are applied via index.html meta tags instead
 */
import React, { createContext, useContext, ReactNode } from 'react';

interface SecurityContextType {
  checkRateLimit: (endpoint: string, maxRequests?: number, windowMinutes?: number) => Promise<any>;
  checkGeolocationAccess: (allowedCountries?: string[]) => Promise<any>;
  triggerIncidentResponse: (type: string, severity: string, description: string) => Promise<any>;
  isSecurityActive: boolean;
}

// Safe default context - security features disabled for mobile compatibility
const defaultContext: SecurityContextType = {
  checkRateLimit: async () => ({ allowed: true }),
  checkGeolocationAccess: async () => ({ access_allowed: true }),
  triggerIncidentResponse: async () => ({ success: true }),
  isSecurityActive: false // Disabled until mobile compatibility issue resolved
};

const SecurityContext = createContext<SecurityContextType>(defaultContext);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  return context || defaultContext;
};

interface SecurityHeadersProviderProps {
  children: ReactNode;
  enableGeoBlocking?: boolean;
  allowedCountries?: string[];
  enableRateMonitoring?: boolean;
}

export const SecurityHeadersProvider: React.FC<SecurityHeadersProviderProps> = ({ 
  children,
  enableGeoBlocking = false,
  allowedCountries = [],
  enableRateMonitoring = false
}) => {
  // NOTE: useSecurityHeaders hook disabled due to mobile compatibility issues
  // Security headers are applied via index.html meta tags instead
  // This provider exists only to maintain component structure
  
  return (
    <SecurityContext.Provider value={defaultContext}>
      {children}
    </SecurityContext.Provider>
  );
};